/** Small shared UI primitives for the admin screens. */
import type { ReactNode } from "react";

export function PageHeader({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}): ReactNode {
  return (
    <div className="mb-5 flex items-center justify-between">
      <h1 className="text-2xl font-semibold text-slate-800">{title}</h1>
      {children}
    </div>
  );
}

const BADGE_TONE: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  accepted: "bg-emerald-100 text-emerald-800",
  approved: "bg-emerald-100 text-emerald-800",
  active: "bg-emerald-100 text-emerald-800",
  revoked: "bg-slate-200 text-slate-600",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-slate-200 text-slate-600",
  expired: "bg-slate-200 text-slate-600",
};

export function StatusBadge({ status }: { status: string }): ReactNode {
  const tone = BADGE_TONE[status] ?? "bg-slate-100 text-slate-700";
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${tone}`}>{status}</span>
  );
}

export function Spinner({ label = "Loading…" }: { label?: string }): ReactNode {
  return (
    <div role="status" className="py-8 text-center text-sm text-slate-500">
      <span className="animate-pulse">{label}</span>
    </div>
  );
}

export function ErrorBanner({ message }: { message: string }): ReactNode {
  return (
    <div role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      {message}
    </div>
  );
}

export function EmptyState({ message }: { message: string }): ReactNode {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
      {message}
    </div>
  );
}
